import { exitImpersonation } from "../../services/impersonation/impersonation.service";
import { useImpersonationSession } from "../../hooks/useImpersonationSession";

/**
 * Persistent red strip shown whenever the most recent API response carried the
 * `X-Impersonation-Session` header (cached by the httpClient interceptor into
 * localStorage). Renders null otherwise, so it is a free render for normal
 * tenant traffic. The Exit button closes the central session, clears local
 * state, and closes this tab — the impersonation tab is always opened with
 * window.open() from the super-admin portal, which stays open in the original
 * tab, so there is nothing to navigate back to here.
 */
const ImpersonationBanner: React.FC = () => {
    const sessionId = useImpersonationSession();

    if (!sessionId) {
        return null;
    }

    const handleExit = async () => {
        try {
            await exitImpersonation();
        } catch {
            // Even if the central exit call fails, clear local state so the
            // operator is never trapped in an impersonated session.
        }

        localStorage.removeItem("token");
        localStorage.removeItem("impersonation_session_id");
        sessionStorage.removeItem("impersonation_boot");
        // Drop the impersonated tenant's slug so it can't leak into a later
        // non-impersonated session on this host.
        localStorage.removeItem("org");

        // Close this tab. It was opened via window.open() from the super-admin
        // portal (which is still open in the original tab), so the browser
        // permits closing it script-side and the operator lands back on the
        // already-open super-admin portal.
        window.close();
    };

    return (
        <div className="bg-error text-white w-full py-2 px-4 flex items-center justify-between gap-2">
            <div className="font-semibold text-sm truncate">
                🔴 Super Admin Mode
                <span className="hidden sm:inline">
                    {" "}— operating as a super admin in this tenant
                </span>
            </div>
            <button
                type="button"
                onClick={handleExit}
                className="btn btn-sm bg-white text-error border-none hover:bg-gray-100"
            >
                Exit Impersonation
            </button>
        </div>
    );
};

export default ImpersonationBanner;
